import { z } from 'zod';
import { route, defineContract } from '../core/types';
import { coerceBooleanFromString, requiredStringSchema } from '../schemas';
import { projectSchema } from '../../interfaces/IProject';
import { organizationSchema } from '../../interfaces/IOrganization';

// ============================================================
// Body Schemas
// ============================================================

const createOrganizationBodySchema = z.object({
	organizationId: requiredStringSchema,
	organizationNick: requiredStringSchema,
});

// ============================================================
// Contract
// ============================================================

/**
 * Organization contract - routes under /organization prefix.
 *
 * IMPORTANT: Specific paths (/all) must come BEFORE parameterized paths (/:organizationId).
 */
export const organizationContract = defineContract('organization', {
	createOrganization: route({
		method: 'POST',
		path: '/',
		body: createOrganizationBodySchema,
		response: z.string(),
		operationId: 'createOrganization',
		tags: ['Organization'],
		summary: 'Create a new organization',
	}),
	getAllOrganizations: route({
		method: 'GET',
		path: '/all',
		response: z.array(organizationSchema),
		operationId: 'getAllOrganizations',
		tags: ['Organization'],
		summary: 'Get all organizations',
	}),
	getOrganizationById: route({
		method: 'GET',
		path: '/:organizationId',
		params: z.object({
			organizationId: requiredStringSchema,
		}),
		response: organizationSchema,
		operationId: 'getOrganizationById',
		tags: ['Organization'],
		summary: 'Get an organization by ID',
	}),
	getProjectsByOrganizationId: route({
		method: 'GET',
		// `:id` before: a generic name the organization-membership guard does not
		// recognize, so this route enumerated any tenant's project list.
		path: '/:organizationId/projects',
		params: z.object({
			organizationId: requiredStringSchema,
		}),
		// `executiveViewEnabled` folded in from `project/executiveViewEnabled`,
		// which was the same organization-scoped roster with one filter applied.
		// One route, one guard, one place the org membership is checked.
		query: z.object({
			executiveViewEnabled: coerceBooleanFromString.optional(),
		}),
		response: z.array(projectSchema),
		operationId: 'getProjectsByOrganizationId',
		tags: ['Organization'],
		summary: "List an organization's projects, optionally only those with executive view enabled",
	}),
	deleteOrganization: route({
		method: 'DELETE',
		path: '/:organizationId',
		params: z.object({
			organizationId: requiredStringSchema,
		}),
		response: z.string(),
		operationId: 'deleteOrganization',
		tags: ['Organization'],
		summary: 'Delete an organization by ID',
	}),
});
